/**
 * SectionHeader — the title that opens a section of a page.
 *
 * The render had thirteen hand-rolled section titles and no primitive; this
 * is the one, ported from the Atlas Universe showroom
 * (`src/frames/operator/SectionHeader`). Structure is the component's,
 * appearance is the brand's: the title takes `--font-heading` with the
 * brand's `--heading-case` / `--heading-tracking` dials, the count is
 * `--ink-3`, and the action is `--link`.
 *
 * A STRUCTURAL primitive (like `Surface` and `Chip`): deliberately not in
 * `PRIMITIVE_METAS` / the manifest — the LLM layout-planner composes content
 * blocks, never chrome (ADR-060).
 */

import type { ReactElement, ReactNode } from 'react';
import styles from './SectionHeader.module.css';

export interface SectionHeaderProps {
  title: ReactNode;
  /** Quiet trailing number — "6", "12 markeder". Rendered tabular. */
  count?: ReactNode;
  /** Right-aligned action ("se alle"). Any node; usually a link or button. */
  action?: ReactNode;
  /**
   * Drop the page gutter when the header already sits inside a padded
   * column. The vertical rhythm stays the component's either way.
   */
  inset?: boolean;
  /** Heading level. Defaults to h2 — a page owns its own h1. */
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
}

export function SectionHeader({
  title,
  count,
  action,
  inset = false,
  as: Heading = 'h2',
  className,
}: SectionHeaderProps): ReactElement {
  const classes = [styles.header, inset ? styles.inset : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <Heading className={styles.title}>{title}</Heading>
      {count != null ? <span className={styles.count}>{count}</span> : null}
      {action != null ? <span className={styles.action}>{action}</span> : null}
    </div>
  );
}

export default SectionHeader;
